import { useCallback, useEffect } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { HeaderThree } from '@/components/layout/HeaderThree'
import { FooterOne, FooterThree } from '@/components/layout/Footer'
import { Offcanvas } from '@/components/layout/Offcanvas'
import { BackToTop } from '@/components/layout/BackToTop'
import { Seo } from '@/components/seo/Seo'

/**
 * Shared chrome for all public marketing pages. Renders the template's
 * page-wrapper + cursors + header/footer/offcanvas and wires the original
 * jQuery/Bootstrap/Swiper/WOW plugins back up after every route render.
 *
 * The branded preloader itself is rendered once at the app root (see
 * `Preloader`) so it also covers the initial mode/settings fetch; this layout
 * simply fades it out once the real page content has mounted.
 */
export function PublicLayout() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isHome = pathname === '/'

  /* Scroll to the top and close the offcanvas whenever the route changes */
  useEffect(() => {
    window.scrollTo({ top: 0 })
    const el = document.getElementById('offcanvas-sidebar')
    const bs = (window as unknown as { bootstrap?: { Offcanvas?: { getInstance: (e: Element) => { hide: () => void } | null } } }).bootstrap
    if (el) {
      bs?.Offcanvas?.getInstance(el)?.hide()
    }
  }, [pathname])

  /* One-time chrome setup + per-route plugin initialisation */
  useEffect(() => {
    window.__fastaidInitChrome?.()
    window.__fastaidInitPage?.()
  }, [pathname])

  /* Fade the preloader out once the real page content has mounted */
  useEffect(() => {
    window.__fastaidPreloader?.()
  }, [])

  /**
   * The template markup contains plain <a href="/..."> links (rewritten from
   * *.html). Intercept them so navigation stays inside the SPA.
   */
  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return

      const anchor = (event.target as HTMLElement).closest('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href) return
      if (anchor.getAttribute('target') === '_blank') return
      if (/^(#|https?:|mailto:|tel:|javascript:)/i.test(href)) return

      if (href.startsWith('/')) {
        event.preventDefault()
        navigate(href)
      }
    },
    [navigate],
  )

  return (
    <div className="page-wrapper" onClick={handleClick}>
      <Seo />

      {/* back to top start */}
      <BackToTop />
      {/* back to top end */}

      {/* mouse cursor start */}
      <div className="mouse-cursor cursor-outer"></div>
      <div className="mouse-cursor cursor-inner"></div>
      {/* mouse cursor end */}

      {/* offcanvas sidebar start */}
      <Offcanvas />
      {/* offcanvas sidebar end */}

      {/* Global header — the Home page (index-3.html) header-3 is the single
          source of truth and is rendered on every public page. */}
      <HeaderThree />

      {/* main start */}
      <main className="main">
        <Outlet />
      </main>
      {/* main end */}

      {isHome ? <FooterThree /> : <FooterOne />}
    </div>
  )
}
